import { Button, Stack } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { UserData, useReg } from "../api/reg/store";
import Input from "../components/Input";
import Loading from "../components/Loading";
import Topbar from "../components/Topbar";
import { getErrorMessage } from "../utils/errors";
import { validatePasswordComplexity } from "../utils/validation";
import { ReactComponent as CheckIcon } from "../svgs/check_badge.svg";
import { ReactComponent as BankIcon } from "../svgs/bank.svg";
import Label from "../components/Label";

const RegistrationSuccessScreen = () => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate("/");
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [navigate]);

    return (
        <div className="center invite-container" style={{ paddingTop: "120px" }}>
            <Stack gap={4} className="align-items-center glassmorphism-card" style={{
                padding: "40px",
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
            }}>
                <div style={{
                    background: "rgba(40, 167, 69, 0.1)",
                    borderRadius: "50%",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <CheckIcon fill="#28a745" width={60} height={60} />
                </div>

                <Stack gap={2} className="text-center">
                    <Label size="28px" fontWeight="700" textAlign="center" padding="0">Registration Success!</Label>
                    <Label size="16px" color="#636e72" textAlign="center" padding="0" wrap={true}>
                        You may now login and use banking online solutions.
                    </Label>
                </Stack>

                <div className="text-center">
                    <p style={{ color: "#0984e3", fontSize: "14px", fontWeight: "500", margin: 0 }}>
                        Redirecting you to login in <strong>{countdown}</strong> seconds...
                    </p>
                </div>

                <Button
                    variant="primary"
                    onClick={() => navigate("/")}
                    style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "12px",
                        fontWeight: "600",
                        boxShadow: "0 4px 15px rgba(9, 132, 227, 0.2)"
                    }}
                >
                    Login Now
                </Button>
            </Stack>
        </div>
    );
}

const FailedReg = ({ title = "Registration Failed", msg = "" }) => {
    return <div className="center invite-container" style={{ paddingTop: "120px" }}>
        <Stack gap={3} className="text-center glassmorphism-card" style={{
            padding: "40px",
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
        }}>
            <Label size="24px" fontWeight="700" textAlign="center" color="#dc3545">{title}</Label>
            <Label size="16px" color="#636e72" textAlign="center" wrap={true}>{msg}</Label>
            <Button variant="outline-primary" onClick={() => window.location.reload()} style={{ marginTop: "10px" }}>Try Again</Button>
        </Stack>
    </div>
}

const defaulUserData: UserData = {
    uname: "",
    pass: "",
    mobile: "",
    email: "",
    displayName: "",
    access: ""
}

const defaultErr: { [key: string]: string } = {}
const defaultRegRes: any = null

// Validation functions
const validateUsername = (username: string): string => {
    if (!username) return "Username is required."
    if (username.length < 6) return "Username must be at least 6 characters."
    if (username.length > 20) return "Username must not exceed 20 characters."
    if (/\s/.test(username)) return "Username cannot contain spaces."
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(username)) return "Username must start with a letter or underscore and contain only letters, numbers, and underscores."
    return ""
}

const validatePassword = (password: string): string => {
    return validatePasswordComplexity(password) || "";
}

const validatePhone = (phone: string): string => {
    if (!phone) return "Phone number is required."
    if (!phone.startsWith("09")) return "Phone number must start with '09'."
    // Remove non-numeric characters for validation
    const cleanPhone = phone.replace(/\D/g, '')
    if (cleanPhone.length < 10) return "Phone number must be at least 10 digits."
    if (cleanPhone.length > 15) return "Phone number is too long."
    // Basic phone number pattern (allows various formats)
    if (!/^[\d\s\-\+\(\)]+$/.test(phone)) return "Invalid phone number format."
    return ""
}


const validateConfirmPassword = (password: string, confirmPassword: string): string => {
    if (!confirmPassword) return "Please confirm your password."
    if (password !== confirmPassword) return "Passwords do not match."
    return ""
}

const RegPage = () => {
    const navigate = useNavigate();
    const { loading, reg } = useReg(state => state)
    const [userData, setUserData] = useState(defaulUserData)
    const [registrationError, setRegistrationRes] = useState(defaultRegRes)
    const [confirmPass, setConfirmPass] = useState("")
    const [errorDetails, setErrorDetails] = useState(defaultErr)
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({})

    const updateData = (id: string, val: string) => {
        setUserData({ ...userData, [id]: val })

        // Clear error for the field being updated
        if (errorDetails[id]) {
            const newErrors = { ...errorDetails }
            delete newErrors[id]
            setErrorDetails(newErrors)
        }
    }

    const validateField = (id: string, value: string) => {
        let error = ""
        switch (id) {
            case "uname":
                error = validateUsername(value)
                break
            case "pass":
                error = validatePassword(value)
                break
            case "mobile":
                error = validatePhone(value)
                break
            case "displayName":
                if (!value) error = "Display name is required."
                break
        }

        if (error) {
            setErrorDetails({ ...errorDetails, [id]: error })
        } else {
            const newErrors = { ...errorDetails }
            delete newErrors[id]
            setErrorDetails(newErrors)
        }
    }

    const handleBlur = (id: string) => {
        setTouched({ ...touched, [id]: true })
        if (id === "confirm") {
            const error = validateConfirmPassword(userData.pass, confirmPass)
            if (error) {
                setErrorDetails({ ...errorDetails, confirm: error })
            } else {
                const newErrors = { ...errorDetails }
                delete newErrors.confirm
                setErrorDetails(newErrors)
            }
        } else {
            validateField(id, userData[id as keyof typeof userData] as string)
        }
    }
    const onRegister = async () => {
        const errDetails = { ...defaultErr }

        // Validate all fields
        const usernameError = validateUsername(userData.uname)
        if (usernameError) errDetails.uname = usernameError

        const passwordError = validatePassword(userData.pass)
        if (passwordError) errDetails.pass = passwordError

        const phoneError = validatePhone(userData.mobile)
        if (phoneError) errDetails.mobile = phoneError

        const confirmError = validateConfirmPassword(userData.pass, confirmPass)
        if (confirmError) errDetails.confirm = confirmError

        if (userData.displayName === "") {
            errDetails.displayName = "Display name is required."
        }
        if (Object.keys(errDetails).length > 0)
            setErrorDetails(errDetails)
        else {
            //console.log("no error")
            setErrorDetails({})
            const responseCode = await reg(userData)
            onRegisterOutput(responseCode)
        }
    }
    const onRegisterOutput = (code: string) => {
        console.log(code)
        if (code === "200") {
            setRegistrationRes("")
            return
        }

        const msg = getErrorMessage(code)
        if (code === "user_already_enrolled") {
            setErrorDetails({ ...errorDetails, "uname": msg })
        } else if (code === "email_already_inuse") {
            setErrorDetails({ ...errorDetails, "email": msg })
        } else if (msg) {
            setRegistrationRes(msg)
        } else {
            setRegistrationRes("Unable to register user because of unknown error.")
        }
    }
    if (registrationError === "") {
        return (
            <>
                <Topbar />
                <RegistrationSuccessScreen />
            </>
        )
    }

    if (registrationError !== null) {
        return (
            <>
                <Topbar />
                <FailedReg msg={registrationError} />
            </>
        )
    }

    return (
        <>
            <Topbar />
            <div className="center invite-container" style={{ paddingTop: "60px", paddingBottom: "60px" }}>
                <Stack gap={4} className="glassmorphism-card" style={{
                    width: "100%",
                    maxWidth: "500px",
                    padding: "40px",
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)"
                }}>
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                        <div style={{
                            background: "rgba(103, 158, 255, 0.1)",
                            borderRadius: "20px",
                            padding: "15px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "15px"
                        }}>
                            <BankIcon fill="#0984e3" width={40} height={40} />
                        </div>
                        <Label size="26px" fontWeight="700" textAlign="center" padding="0">Create Your Account</Label>
                        <Label size="14px" color="#636e72" textAlign="center" padding="5px 0 0" wrap={true}>
                            Join CoreOne Banking and start managing your finances securely.
                        </Label>
                    </div>

                    <Stack gap={3}>
                        <div>
                            <Input
                                disabled={loading}
                                error={errorDetails.displayName !== ""}
                                label=""
                                placeholder="Display Name"
                                onChange={(e: any) => updateData("displayName", e.currentTarget.value)}
                                onBlur={() => handleBlur("displayName")}
                            />
                            {errorDetails.displayName && <Label size="12px" color="#dc3545" padding="4px 0" wrap={true}>{errorDetails.displayName}</Label>}
                        </div>

                        <div>
                            <Input
                                disabled={loading}
                                error={errorDetails.mobile !== ""}
                                label=""
                                placeholder="Phone (e.g., 09153704092)"
                                onChange={(e: any) => updateData("mobile", e.currentTarget.value)}
                                onBlur={() => handleBlur("mobile")}
                            />
                            {errorDetails.mobile ?
                                <Label size="12px" color="#dc3545" padding="4px 0" wrap={true}>{errorDetails.mobile}</Label> :
                                !touched.mobile && <Label size="11px" color="#636e72" padding="4px 0">10-15 digits, starts with 09</Label>
                            }
                        </div>

                        <div>
                            <Input
                                disabled={loading}
                                error={errorDetails.uname !== ""}
                                label=""
                                placeholder="Username"
                                onChange={(e: any) => updateData("uname", e.currentTarget.value)}
                                onBlur={() => handleBlur("uname")}
                            />
                            {errorDetails.uname ?
                                <Label size="12px" color="#dc3545" padding="4px 0" wrap={true}>{errorDetails.uname}</Label> :
                                !touched.uname && <Label size="11px" color="#636e72" padding="4px 0">6-20 chars, alphanumeric</Label>
                            }
                        </div>

                        <div>
                            <Input
                                disabled={loading}
                                error={errorDetails.pass !== ""}
                                label=""
                                placeholder="Password"
                                type="password"
                                onChange={(e: any) => updateData("pass", e.currentTarget.value)}
                                onBlur={() => handleBlur("pass")}
                            />
                            {errorDetails.pass ?
                                <Label size="12px" color="#dc3545" padding="4px 0" wrap={true}>{errorDetails.pass}</Label> :
                                !touched.pass && <Label size="11px" color="#636e72" padding="4px 0">Min 8 chars, mixed case & numbers</Label>
                            }
                        </div>

                        <div>
                            <Input
                                disabled={loading}
                                error={errorDetails.confirm !== ""}
                                label=""
                                placeholder="Confirm Password"
                                type="password"
                                onChange={(e: any) => {
                                    setConfirmPass(e.currentTarget.value)
                                    if (errorDetails.confirm) {
                                        const newErrors = { ...errorDetails }
                                        delete newErrors.confirm
                                        setErrorDetails(newErrors)
                                    }
                                }}
                                onBlur={() => handleBlur("confirm")}
                            />
                            {errorDetails.confirm && <Label size="12px" color="#dc3545" padding="4px 0" wrap={true}>{errorDetails.confirm}</Label>}
                        </div>
                    </Stack>

                    <Stack gap={3} style={{ marginTop: "10px" }}>
                        <Button
                            disabled={loading}
                            onClick={onRegister}
                            style={{
                                width: "100%",
                                padding: "14px",
                                borderRadius: "12px",
                                fontWeight: "600",
                                background: "#0984e3",
                                border: "none",
                                boxShadow: "0 4px 15px rgba(9, 132, 227, 0.2)"
                            }}
                        >
                            {!loading ? "Create Account" : <Loading />}
                        </Button>
                        <Button
                            variant="link"
                            onClick={() => navigate("/")}
                            style={{ color: "#636e72", textDecoration: "none", fontSize: "14px" }}
                        >
                            Already have an account? Log in
                        </Button>
                    </Stack>
                </Stack>
            </div>
        </>
    )
}

export default RegPage;